import Vue from 'vue';
import candidate from "services/candidate";
import finances from "services/finances";
import * as types from "../mutation-types";
import router from '../../../routes'

const state = {
  data: {
    candidates: [
      {
        applications: {},
        events: {},
        candidateDocuments: [] // Documents at candidate level (identity, diplomas, etc.)
      }
    ]
  },
  comments: [],
  events: {},
  message: null,
  existingAccount: null,
  status: null,
  pending: false,
  timeline: {
    items: [],
    page: 0,
    perPage: 30,
    total: 0,
    hasMore: false,
    errors: {},
    openSchedules: [],
    upcomingInterviews: [],
    upcomingAppointments: [],
    applicationMarkers: [],
    callSummary: [],
    includePassive: false,
    view: 'minimal',
    canViewFull: false,
    candidateId: null,
    loading: false,
  },
};

const getters = {
  accountId: state => state.data.id,
  getCandidateAccount: state => state.data,
  getCandidateById: state => id => {
    return state.data.candidates.find(candidate => candidate.id === id);
  },
  hasCandidates: state => {
    return state.data.candidates && state.data.candidates.length > 1;
  },
  hasRegisteredApplication: state => id => {
    let candidate = state.data.candidates.find(
      candidate => candidate.id === id
    );

    return candidate.hasRegisteredApplication;
  },
  hasSchoolingCandidates: state => {
    if (state.data.candidates && state.data.candidates.length > 1) {
      return state.data.candidates.some(
        candidate => candidate.applications.accepted
      );
    }
  },
  getCandidates(state) {
    return state.data.candidates;
  },
  activeCandidates(state) {
    if (state.data.candidates) {
      return state.data.candidates.filter(function(candidate) {
        return !candidate.newCandidate;
      });
    }
  },
  candidateApplication: state => selected => {
    if (state.data.candidates) {
      let candidate = state.data.candidates.find(
        candidate => candidate.id === selected.id
      );
      if (candidate) {
        return candidate.applications;
      }
    }
  },
  candidateApplicationChoices: state => selected => {
    if (state.data.candidates) {
      let candidate = state.data.candidates.find(
        candidate => candidate.id === selected.id
      );
      if (candidate && candidate.applications.choices) {
        return candidate.applications.choices[0];
      }
    }
  },
  getSingleCandidate(state) {
    if (state.data.candidates) {
      return state.data.candidates[0];
    }
  },
  getSingleApplication(state) {
    if (state.data.candidates) {
      return state.data.candidates[0].applications;
    }
  },
  getStatus: state => state.status,
  getMessage: state => state.message,
  getExistingAccount: state => state.existingAccount,
  getPending: state => state.pending,
  getComments: state => state.comments,
  getEvents: state => state.events,

  // Candidate-level documents getters
  getCandidateDocuments: state => candidateId => {
    const candidate = state.data.candidates.find(c => c.id === candidateId);
    return candidate?.candidateDocuments || [];
  },
  getCandidateDocumentsByType: state => (candidateId, docType) => {
    const candidate = state.data.candidates.find(c => c.id === candidateId);
    if (!candidate?.candidateDocuments) return [];
    return candidate.candidateDocuments.filter(doc => doc.document_type === docType);
  },
  getAllDocuments: state => candidateId => {
    const candidate = state.data.candidates.find(c => c.id === candidateId);
    if (!candidate) return [];
    // Merge candidate-level docs and application-level docs
    const candidateDocs = candidate.candidateDocuments || [];
    const appDocs = candidate.applications?.documents || [];
    return [...candidateDocs, ...appDocs];
  },
  isApplicationReadOnly: state => (candidateId, applicationId) => {
    const candidate = state.data.candidates.find(c => c.id === candidateId);
    if (!candidate) return true;
    // Current application is not read-only
    if (candidate.applications?.id === applicationId) return false;
    // All historical applications are read-only
    return true;
  },
  getHistoricalApplications: state => candidateId => {
    const candidate = state.data.candidates.find(c => c.id === candidateId);
    if (!candidate?.application_log) return [];
    return candidate.application_log.filter(app => app.isReadOnly !== false);
  }
};

const mutations = {
  [types.PENDING](state) {
    state.pending = true;
    state.existingAccount = null;
  },
  [types.ACCOUNT_MSG](state, payload) {
    state.message = payload.statusText;
    if (payload.data && payload.data.message) {
      state.message = payload.data.message;
    }
    state.existingAccount = (payload.data && payload.data.existing_account) || null;
    state.status = payload.status;
    state.pending = false;
  },

  [types.SET_CANDIDATE](state, payload) {
    state.data = payload;
    if (
      state.data.candidates &&
      state.data.candidates.length <= 8 &&
      !state.data.candidates.some(candidate => candidate.newCandidate)
    ) {
      let newCandidate = {};
      if (state.data.candidates.length) {
        newCandidate = JSON.parse(JSON.stringify(state.data.candidates[0]));
      }

      newCandidate.newCandidate = true;
      newCandidate.id = null;
      newCandidate.first_name = null;
      newCandidate.last_name = null;
      newCandidate.gender = null;
      newCandidate.birthday = null;
      newCandidate.birth_place = null;
      newCandidate.nir_number = null;
      newCandidate.passport_number = null;
      newCandidate.passport_validity = null;
      newCandidate.profile_photo = null;
      newCandidate.account_complete = false;
      newCandidate.choices_complete = false;
      newCandidate.curriculum = null;
      newCandidate.curriculum_complete = false;
      newCandidate.application_step = null;
      newCandidate.hasAcceptedApplication = false;
      newCandidate.hasRegisteredApplication = false;
      newCandidate.application_log = [];
      newCandidate.applications = {};
      newCandidate.documents = {};
      newCandidate.credits = {};



      if (state.data.gender && state.data.address) {
        newCandidate.address = state.data.address;
        newCandidate.email = state.data.email;
        newCandidate.address_complement = state.data.address_complement;
        newCandidate.city = state.data.city;
        newCandidate.post_code = state.data.post_code;
        newCandidate.phone = state.data.phone;
        newCandidate.phone_code = state.data.phone_code;
        newCandidate.country = state.data.country;
        newCandidate.tutor = state.data.gender === 'M' ? 'father':'mother';
        newCandidate.parents = [];

        let parent = {};
        parent.role = state.data.gender === 'M' ? 'father':'mother';
        parent.first_name = state.data.first_name;
        parent.last_name = state.data.last_name;
        parent.address = state.data.address;
        parent.email = state.data.email;
        parent.address_complement = state.data.address_complement;
        parent.city = state.data.city;
        parent.post_code = state.data.post_code;
        parent.phone = state.data.phone;
        parent.phone_code = state.data.phone_code;
        parent.country = state.data.country;
        parent.job = null;
        newCandidate.parents.push(parent);
      }


      state.data.candidates.push(newCandidate);
    }
  },
  [types.NEW_CANDIDATE](state) {
    const newCandidate = JSON.parse(JSON.stringify(state.data.candidates[0]));
    newCandidate.newCandidate = true;
    newCandidate.id = null;
    newCandidate.first_name = null;
    newCandidate.last_name = null;
    newCandidate.gender = null;
    newCandidate.birthday = null;
    newCandidate.birth_place = null;
    newCandidate.nir_number = null;
    newCandidate.passport_number = null;
    newCandidate.passport_validity = null;
    newCandidate.profile_photo = null;
    newCandidate.account_complete = false;
    newCandidate.choices_complete = false;
    newCandidate.curriculum = null;
    newCandidate.curriculum_complete = false;
    newCandidate.application_step = null;
    newCandidate.hasAcceptedApplication = false;
    newCandidate.hasRegisteredApplication = false;
    newCandidate.application_log = [];
    newCandidate.applications = {};
    newCandidate.documents = {};

    state.data.candidates.push(newCandidate);
  },
  [types.DELETE_CANDIDATE](state, candidate) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === candidate) {
        const index = state.data.candidates.indexOf(obj);
        state.data.candidates.splice(index, 1);
      }
    });
  },
  [types.RESET_CANDIDATE](state) {
    state.data = {
      candidates: []
    };

    const newCandidate = {};
    newCandidate.newCandidate = true;
    newCandidate.school_type = null;
    newCandidate.id = null;
    newCandidate.first_name = null;
    newCandidate.last_name = null;
    newCandidate.gender = null;
    newCandidate.birthday = null;
    newCandidate.birth_place = null;
    newCandidate.nir_number = null;
    newCandidate.passport_number = null;
    newCandidate.passport_validity = null;
    newCandidate.profile_photo = null;
    newCandidate.account_complete = false;
    newCandidate.choices_complete = false;
    newCandidate.curriculum = null;
    newCandidate.curriculum_complete = false;
    newCandidate.application_step = null;
    newCandidate.hasAcceptedApplication = false;
    newCandidate.hasRegisteredApplication = false;
    newCandidate.application_log = [];
    newCandidate.applications = {};
    newCandidate.documents = {};

    state.data.candidates.push(newCandidate);
    state.comments = {};
    state.message = null;
    state.existingAccount = null;
    state.status = null;
    state.pending = false;
  },
  [types.SET_APPLICATION](state, application) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === application.candidate_id) {
        obj.applications = application;
      }
    });

    state.pending = false;
  },

  [types.SEND_CURRICULUM](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id) {
        obj.curriculum = payload;
        obj.curriculum_complete = true;
      }
    });
    state.pending = false;
  },
  [types.SEND_MOTIVATION](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id) {
        obj.applications = payload;
      }
    });
    state.pending = false;
  },
  [types.SEND_DOCUMENT](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id) {
        obj.applications.documents = payload.application_documents;
      }
    });
    state.pending = false;
  },
  [types.REMOVE_DOCUMENT](state, doc) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === doc.application_id) {
        const index = obj.applications.documents.indexOf(doc);
        obj.applications.documents.splice(index, 1);
      }
    });
  },
  [types.REMOVE_FUNDING_DOCUMENT](state, doc) {
    state.data.candidates.filter(function(obj) {
      obj.funding.funding_orgas.filter(function(org) {
        if (org.id === doc.id) {
          const index = obj.funding.funding_orgas.indexOf(doc);
          obj.funding.funding_orgas.splice(index, 1);
        }
      })

    });
  },
  [types.SEND_PROCESS_MODE](state, payload) {
    state.pending = false;
  },
  [types.SEND_PAYMENT_MODE](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.id) {
        obj.applications = payload;
      }
    });
    state.pending = false;
  },
  [types.INTERVIEW_APPOINTMENT](state, payload) {
    state.pending = false;
  },
  [types.ACCEPT_APPLICATION](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.id) {
        obj.applications = payload;
      }
    });
    state.pending = false;
  },
  [types.CHECKOUT](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.application_id) {
        obj.applications.checkout = payload;
      }
    });
  },

  [types.SEND_MESSAGE](state, payload) {
    let candidate = state.data.candidates.find(
      candidate => candidate.id === payload.application.candidate_id
    );

    if (candidate.applications.messages) {
      candidate.applications.messages.push(payload);
    } else {
      candidate.application_log[0].messages.push(payload);
    }

    state.pending = false;
  },
  [types.REPLY_MESSAGE](state, payload) {
    let candidate = state.data.candidates.find(
      candidate => candidate.id === payload.application.candidate_id
    );
    let message = candidate.applications.messages.find(
      message => message.id === payload.id
    );

    const index = candidate.applications.messages.indexOf(message);
    candidate.applications.messages[index] = payload;

    state.pending = false;
  },
  [types.FETCH_COMMENTS](state, payload) {
    state.comments = payload;
    state.pending = false;
  },
  [types.SET_TIMELINE](state, { candidateId, includePassive, response }) {
    const meta = response.meta || {};
    state.timeline = {
      items: response.data || [],
      page: meta.page || 1,
      perPage: meta.per_page || state.timeline.perPage,
      total: meta.total || 0,
      hasMore: meta.has_more || false,
      errors: meta.errors || {},
      openSchedules: meta.open_schedules || [],
      upcomingInterviews: meta.upcoming_interviews || [],
      upcomingAppointments: meta.upcoming_appointments || [],
      applicationMarkers: meta.application_markers || [],
      callSummary: meta.call_summary || [],
      includePassive,
      view: meta.view || 'minimal',
      canViewFull: !!meta.can_view_full,
      candidateId,
      loading: false,
    };
    state.pending = false;
  },
  [types.APPEND_TIMELINE](state, response) {
    const items = response.data || [];
    const meta = response.meta || {};
    state.timeline.items = state.timeline.items.concat(items);
    state.timeline.page = meta.page || state.timeline.page;
    state.timeline.total = meta.total !== undefined ? meta.total : state.timeline.total;
    state.timeline.hasMore = meta.has_more || false;
    state.timeline.errors = meta.errors || {};
    state.timeline.view = meta.view || state.timeline.view;
    state.timeline.canViewFull = meta.can_view_full !== undefined ? !!meta.can_view_full : state.timeline.canViewFull;
    if (meta.open_schedules) {
      state.timeline.openSchedules = meta.open_schedules;
    }
    state.timeline.loading = false;
    state.pending = false;
  },
  [types.APPEND_TIMELINE_ITEM](state, item) {
    state.timeline.items.unshift(item);
    state.timeline.total += 1;
    state.pending = false;
  },
  [types.SET_TIMELINE_LOADING](state, loading) {
    state.timeline.loading = !!loading;
  },
  [types.SET_TIMELINE_INCLUDE_PASSIVE](state, includePassive) {
    state.timeline.includePassive = !!includePassive;
  },
  [types.RESET_TIMELINE](state) {
    state.timeline = {
      items: [],
      page: 0,
      perPage: state.timeline.perPage,
      total: 0,
      hasMore: false,
      errors: {},
      openSchedules: [],
      upcomingInterviews: [],
      upcomingAppointments: [],
      applicationMarkers: [],
      callSummary: [],
      includePassive: false,
      view: 'minimal',
      canViewFull: false,
      candidateId: null,
      loading: false,
    };
  },
  [types.FETCH_CANDIDATE_DOCUMENTS](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id) {
        obj.documents = payload.documents;
      }
    });
    state.pending = false;
  },
  [types.SET_CANDIDATE_DOCUMENTS](state, payload) {
    const documentsPayload = payload.data || payload;
    const candidate = state.data.candidates.find(obj => obj.id === documentsPayload.id);
    if (candidate) {
      Vue.set(candidate, 'candidateDocuments', documentsPayload.documents || []);
    }
    state.pending = false;
  },
  [types.ADD_CANDIDATE_DOCUMENT](state, payload) {
    const candidate = state.data.candidates.find(obj => obj.id === payload.candidate_id);
    if (candidate) {
      if (!candidate.candidateDocuments) {
        Vue.set(candidate, 'candidateDocuments', []);
      }
      candidate.candidateDocuments.push(payload.document);
    }
    state.pending = false;
  },
  [types.REMOVE_CANDIDATE_DOCUMENT](state, payload) {
    const candidate = state.data.candidates.find(obj => obj.id === payload.candidate_id);
    if (candidate && candidate.candidateDocuments) {
      const index = candidate.candidateDocuments.findIndex(doc => {
        const sourceMatches = payload.source ? (doc.source || 'candidate') === payload.source : true;
        return doc.id === payload.document_id && sourceMatches;
      });
      if (index !== -1) {
        candidate.candidateDocuments.splice(index, 1);
      }
    }
    if (candidate && candidate.applications && Array.isArray(candidate.applications.documents)) {
      const index = candidate.applications.documents.findIndex(doc => doc.id === payload.document_id);
      if (index !== -1) {
        candidate.applications.documents.splice(index, 1);
      }
    }
    state.pending = false;
  },
  [types.SEND_COMMENT](state, payload) {
    state.comments.unshift(payload);
    if (payload.schedule) {
      state.data.schedules.unshift(payload.schedule);
    }
    state.pending = false;
  },
  [types.DELETE_COMMENT](state, payload) {
    state.comments.splice(state.comments.indexOf(payload), 1);
    state.pending = false;
  },
  [types.FETCH_CANDIDATE_EVENTS](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (payload.length) {
        if (obj.id === payload[0].candidate_id) {
          obj.events = payload;
        }
      } else {
        obj.events = payload;
      }
    });
    state.pending = false;
  },
  [types.CANDIDATE_EVENT](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id) {
        obj.events.push(payload);
      }
    });
    state.pending = false;
  },
  [types.RECEIVED_PAYMENT](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.id) {
        obj.applications = payload;
      }
    });
    state.pending = false;
  },
  [types.RECEIVED_REGISTRATION](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.application_id) {
        obj.applications.registration = payload;
      }
    });
    state.pending = false;
  },
  [types.UPDATE_APPLICATION](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.id) {
        obj.applications = payload;
      }
    });
    state.pending = false;
  },
  [types.UPDATE_APPLICATION_FIELDS](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.id === payload.candidate_id && obj.applications) {
        if (payload.sponsoring !== undefined) Vue.set(obj.applications, 'sponsoring', payload.sponsoring);
        if (payload.disability !== undefined) Vue.set(obj.applications, 'disability', payload.disability);
      }
    });
  },
  setStatus: (state, payload) => {
    state.status = payload;
  },
  [types.UPDATE_REGISTRATION](state, payload) {
    state.data.candidates.filter(function(obj) {
      if (obj.applications.id === payload.application_id) {
        obj.applications.registration = payload;
      }
    });
    state.pending = false;
  },
  deleteSchedule: (state, schedule) => {
    state.data.schedules.filter(function(obj) {
      if (obj.id === schedule.id) {
        const index = state.data.schedules.indexOf(obj);
        state.data.schedules.splice(index, 1);
      }
    });
  },
  [types.UPDATE_COMMENT](state, payload) {
    const index = state.comments.findIndex(comment => comment.id === payload.id);
    if (index !== -1) {
      state.comments[index] = payload;
    }
  },
};

const actions = {
  async resetCandidate({ commit }) {
    commit(types.RESET_CANDIDATE);
  },
  async newCandidate({ commit }) {
    commit(types.NEW_CANDIDATE);
  },
  async selectApplication({ commit }, application) {
    commit(types.SET_APPLICATION, application);
  },

  // Add/Update clients users accounts
  async newUserAccount({ commit }, contact) {
    commit(types.PENDING);
    const payload = await candidate.newUserAccount(contact);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateUserAccount({ commit }, account) {
    commit(types.PENDING);
    const payload = await candidate.updateUserAccount(account);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async newAccountCandidate({ commit }, contact) {
    commit(types.PENDING);
    const payload = await candidate.newCandidate(contact);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async simpleFormCandidate({ commit }, contact) {
    commit(types.PENDING);
    const payload = await candidate.simpleFormCandidate(contact);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async addCandidate({ commit }, account) {
    commit(types.PENDING);
    const payload = await candidate.addCandidate(account);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addShopCandidate({ commit }, account) {
    commit(types.PENDING);
    const payload = await candidate.addShopCandidate(account);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteCandidate({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.deleteCandidate(data);
    if ((await payload.status) === 200) {
      router.replace({ name: 'list-applications' })
      commit(types.DELETE_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async archiveCandidate({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.archiveCandidate(data);
    if ((await payload.status) === 200) {
      router.replace({ name: 'list-applications' })
      commit(types.DELETE_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async resetPasswordCandidate({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.resetPasswordCandidate(data);
    if ((await payload.status) === 200) {
      commit(types.DELETE_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async restoreCandidate({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.archiveCandidate(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async suspendApplication({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.suspendApplication(data);
    if ((await payload.status) === 200) {
      commit(types.SET_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async resumeApplication({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.resumeApplication(data);
    if ((await payload.status) === 200) {
      commit(types.SET_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteApplication({ commit }, applicationId) {
    commit(types.PENDING);
    const payload = await candidate.deleteApplication(applicationId);
    commit(types.ACCOUNT_MSG, await payload);

    if (payload.status !== 200) {
      // En cas d'erreur, on rejette la promesse avec le message d'erreur
      const errorMessage = payload.data?.message || 'Erreur lors de la suppression de la candidature';
      throw new Error(errorMessage);
    }

    return payload;
  },
  async fetchContact({ commit }, id) {
    commit(types.PENDING);
    const payload = await candidate.getContact(id);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async updateContact({ commit }, account) {
    commit(types.PENDING);
    const payload = await candidate.updateCandidate(account);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async saveCurriculum({ commit }, curriculum) {
    commit(types.PENDING);
    const payload = await candidate.saveCurriculum(curriculum);
    if ((await payload.status) === 200) {
      commit(types.SEND_CURRICULUM, await payload.data);
      commit(types.UPDATE_APPLICATION_FIELDS, {
        candidate_id: curriculum.candidate_id,
        sponsoring: curriculum.sponsoring,
        disability: curriculum.disability
      });
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveDisability({ commit }, disabilityData) {
    commit(types.PENDING);
    // Utiliser le même endpoint que saveCurriculum mais avec seulement les données du handicap
    const payload = await candidate.saveCurriculum(disabilityData);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveChoices({ commit }, choices) {
    commit(types.PENDING);
    const payload = await candidate.saveChoices(choices);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveMotivation({ commit }, motivation) {
    commit(types.PENDING);
    const payload = await candidate.saveMotivation(motivation);
    if ((await payload.status) === 200) {
      commit(types.SEND_MOTIVATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async savePaymentMode({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.savePaymentMode(payment);
    if ((await payload.status) === 200) {
      commit(types.SEND_PAYMENT_MODE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async documentComplete({ commit }, application) {
    const payload = await candidate.documentComplete(application);
    if ((await payload.status) === 200) {
      commit(types.SEND_DOCUMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async documentVisa({ commit }, application) {
    const payload = await candidate.documentVisa(application);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async updateCampusFrance({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.updateCampusFrance(data);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async documentSponsoring({ commit }, application) {
    const payload = await candidate.documentSponsoring(application);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fundingConfirmation({ commit }, application) {
    const payload = await candidate.fundingConfirmation(application);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fundingAdminValidation({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.fundingAdminValidation(payment);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fundingDocRemove({ commit }, doc) {
    commit(types.REMOVE_FUNDING_DOCUMENT, doc);
  },
  async addressVisa({ commit }, application) {
    const payload = await candidate.addressVisa(application);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async validVisaDocument({ commit }, { candidateId, applicationId }) {
    commit(types.PENDING);
    const payload = await candidate.validVisaDocument({ candidateId, applicationId });
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async invalidVisaDocument({ commit }, { candidateId, applicationId, reason }) {
    commit(types.PENDING);
    const payload = await candidate.invalidVisaDocument({ candidateId, applicationId, reason });
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async sendVisaRequestCertificate({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.sendVisaRequestCertificate(payment);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async validResidencePermit({ commit }, { candidateId, applicationId }) {
    commit(types.PENDING);
    const payload = await candidate.validResidencePermit({ candidateId, applicationId });
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async invalidResidencePermit({ commit }, { candidateId, applicationId }) {
    commit(types.PENDING);
    const payload = await candidate.invalidResidencePermit({ candidateId, applicationId });
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async resetVisaValidation({ commit }, { candidateId, applicationId }) {
    commit(types.PENDING);
    const payload = await candidate.resetVisaValidation({ candidateId, applicationId });
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async validateSponsoringDocument({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.validateSponsoringDocument(payment);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async invalidSponsoringDocument({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.invalidSponsoringDocument(payment);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveProcessMode({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.saveProcessMode(payment);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptPayment({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptPayment(registration);
    if ((await payload.status) === 200) {
      commit(types.RECEIVED_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveRegistrationFunding({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.saveRegistrationFunding(payment);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveApplicationStatus({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.saveApplicationStatus(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveFinanceStatus({ commit }, status) {
    commit(types.PENDING);
    const payload = await finances.saveFinanceStatus(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fetchContactFinance({ commit }, id) {
    commit(types.PENDING);
    const payload = await finances.getContactFinance(id);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updatePayments({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.updatePayment(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deletePayments({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.deletePayment(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addPayment({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.addPayment(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async applicationAccountingReviewed({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.applicationAccountingReviewed(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async orderAccountingReviewed({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.orderAccountingReviewed(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cashflowYearAccountingReviewed({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.cashflowYearAccountingReviewed(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateBilling({ commit }, data) {
    commit(types.PENDING);
    try {
      const payload = await finances.updateBilling(data);
      if (payload.status === 200) {
        commit(types.SET_CANDIDATE, payload.data);
        commit(types.ACCOUNT_MSG, payload);
        return {success: true, status: payload.status};
      }
      return {success: false, status: payload.status};
    } catch (error) {
      commit(types.ACCOUNT_MSG, error);
      return {success: false};
    }
  },
  async addBilling({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.addBilling(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteBilling({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.deleteBilling(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addCredit({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.addCredit(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateCredit({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.updateCredit(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteCredit({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.deleteCredit(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async addPromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.addPromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updatePromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.updatePromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deletePromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.deletePromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setApplicationPromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.setApplicationPromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async removeApplicationPromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.removeApplicationPromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setOrderPromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.setOrderPromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async removeOrderPromo({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.removeOrderPromo(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },

  async switchPaymentLocation({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.switchPaymentLocation(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateReport({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.updateReport(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addReport({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.addReport(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteReport({ commit }, data) {
    commit(types.PENDING);
    const payload = await finances.deleteReport(data);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  async saveFinanceProcess({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.saveFinanceProcess(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteFinanceProcess({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.deleteFinanceProcess(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveApplicationReminder({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.saveApplicationReminder(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteApplicationReminder({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.deleteApplicationReminder(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveApplicationSchedule({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.saveApplicationSchedule(status);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteApplicationSchedule({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.deleteApplicationSchedule(status);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateSchedule({ commit }, schedule) {
    commit(types.PENDING);
    const payload = await candidate.updateSchedule(schedule);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateScheduleAppointment({ commit }, schedule) {
    commit(types.PENDING);
    const payload = await candidate.updateScheduleAppointment(schedule);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async markAppointmentsAsCompleted({ commit }, schedule) {
    commit(types.PENDING);
    const payload = await candidate.markAppointmentsAsCompleted(schedule);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteSchedule({ commit }, schedule) {
    commit(types.PENDING);
    const payload = await candidate.deleteSchedule(schedule);
    if ((await payload.status) === 200) {
      commit("deleteSchedule", schedule);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveFinanceSchedule({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.saveFinanceSchedule(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteFinanceSchedule({ commit }, status) {
    commit(types.PENDING);
    const payload = await candidate.deleteFinanceSchedule(status);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addTask({ commit }, task) {
    commit(types.PENDING);
    const payload = await candidate.addTask(task);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteTask({ commit }, task) {
    commit(types.PENDING);
    const payload = await candidate.deleteTask(task);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async doneTask({ commit }, task) {
    commit(types.PENDING);
    const payload = await candidate.doneTask(task);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async removeDocument({ commit }, doc) {
    commit(types.REMOVE_DOCUMENT, doc);
  },
  async setInterviewAppointment({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.setInterviewAppointment(application);
    if ((await payload.status) === 200) {
      commit(types.INTERVIEW_APPOINTMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setInterviewAppointmentDate({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.setInterviewAppointmentDate(application);
    if ((await payload.status) === 200) {
      commit(types.INTERVIEW_APPOINTMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setInterviewAppointmentLink({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.setInterviewAppointmentLink(application);
    if ((await payload.status) === 200) {
      commit(types.INTERVIEW_APPOINTMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async setInterviewComment({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.setInterviewComment(data);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async declareInterviewDone({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.declareInterviewDone(data);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async attachInterviewReport({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.attachInterviewReport(data);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async cancelInterviewDeclaration({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.cancelInterviewDeclaration(data);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async acceptApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.acceptApplication(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
    if (payload.status !== 200) {
      throw new Error((payload.data && payload.data.message) || "L'acceptation de la candidature a échoué.");
    }
  },
  async refuseApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.refuseApplication(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelAcceptedApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.cancelAcceptedApplication(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelCompletedApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.cancelCompletedApplication(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.cancelApplication(application);
    if ((await payload.status) === 200) {
      commit(types.SET_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelAdmissionGraduated({ commit }, { applicationId, actions, reason }) {
    commit(types.PENDING);
    const payload = await candidate.cancelAdmissionGraduated(applicationId, actions, reason);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async detachOrderCredit({ commit }, orderId) {
    commit(types.PENDING);
    const payload = await candidate.detachOrderCredit(orderId);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async fetchOrderAvailableCredits({ commit }, orderId) {
    const payload = await candidate.fetchOrderAvailableCredits(orderId);
    return payload;
  },
  async attachOrderCredit({ commit }, { orderId, creditId, amount }) {
    commit(types.PENDING);
    const payload = await candidate.attachOrderCredit(orderId, creditId, amount);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async uncancelApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.uncancelApplication(application);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async hasToPayFeesApplication({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.hasToPayFeesApplication(application);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async optionAlternance({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.optionAlternance(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelOptionAlternance({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.cancelOptionAlternance(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async previewCandidatePaceCorrection({ commit }, data) {
    const payload = await candidate.previewCandidatePaceCorrection(data);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async fetchCandidatePaceCorrectionHistory({ commit }, applicationId) {
    const payload = await candidate.candidatePaceCorrectionHistory(applicationId);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async correctCandidatePace({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.correctCandidatePace(data);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async retryCandidatePaceSynchronization({ commit }, data) {
    commit(types.PENDING);
    const payload = await candidate.retryCandidatePaceSynchronization(data);
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async alternanceRequest({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.alternanceRequest(application);
    if ((await payload.status) === 200) {
      commit(types.ACCEPT_APPLICATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fetchCheckout({ commit }, applicationId) {
    const payload = await candidate.fetchCheckout(applicationId);
    if ((await payload.status) === 200) {
      commit(types.CHECKOUT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async sendMessage({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.sendMessage(message);
    if ((await payload.status) === 200) {
      // commit(types.SEND_MESSAGE, await payload.data);
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async replyMessage({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.sendMessage(message);
    if ((await payload.status) === 200) {
      // commit(types.REPLY_MESSAGE, await payload.data);
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fetchComments({ commit }, application) {
    commit(types.PENDING);
    const payload = await candidate.fetchComments(application);
    if ((await payload.status) === 200) {
      commit(types.FETCH_COMMENTS, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fetchTimeline({ commit }, { candidateId, page = 1, includePassive = false, includeBulkMessages = false, view = 'minimal', perPage = 30 } = {}) {
    commit(types.SET_TIMELINE_LOADING, true);
    if (page === 1) {
      commit(types.PENDING);
    }
    const payload = await candidate.fetchTimeline(candidateId, {
      page,
      perPage,
      includePassive,
      includeBulkMessages,
      view,
    });
    if (payload && payload.status === 200) {
      if (page === 1) {
        commit(types.SET_TIMELINE, {
          candidateId,
          includePassive,
          response: payload.data,
        });
      } else {
        commit(types.APPEND_TIMELINE, payload.data);
      }
      return { ok: true, data: payload.data };
    }
    commit(types.SET_TIMELINE_LOADING, false);
    commit(types.ACCOUNT_MSG, payload);
    return { ok: false, response: payload };
  },
  async composeTimelineAction({ commit }, { candidateId, payload }) {
    commit(types.PENDING);
    const response = await candidate.composeTimelineAction(candidateId, payload);
    if (response && response.status === 200) {
      const item = response.data && response.data.data ? response.data.data : null;
      if (item) {
        commit(types.APPEND_TIMELINE_ITEM, {
          id: 'compose:' + (item.comment ? item.comment.id : Date.now()),
          type: 'comment',
          occurred_at: item.comment ? item.comment.date : null,
          payload: item.comment,
          actor: null,
          composed: item,
        });
      }
      return { ok: true, data: response.data };
    }
    commit(types.ACCOUNT_MSG, response);
    return { ok: false, response };
  },
  setTimelineIncludePassive({ commit }, value) {
    commit(types.SET_TIMELINE_INCLUDE_PASSIVE, value);
  },
  resetTimeline({ commit }) {
    commit(types.RESET_TIMELINE);
  },
  async markEventAttended(_, { candidateId, candidateEventId }) {
    const response = await candidate.markEventAttended(candidateId, candidateEventId);
    if (response && response.status === 200) {
      return { ok: true, data: response.data };
    }
    return { ok: false, response };
  },
  async deleteTimelineComment(_, { candidateId, commentId }) {
    const response = await candidate.deleteTimelineComment(candidateId, commentId);
    if (response && response.status === 200) {
      return { ok: true, data: response.data };
    }
    return { ok: false, response };
  },
  async closeTimelineSchedule(_, { candidateId, scheduleId }) {
    const response = await candidate.closeTimelineSchedule(candidateId, scheduleId);
    if (response && response.status === 200) {
      return { ok: true, data: response.data };
    }
    return { ok: false, response };
  },
  async rescheduleTimelineSchedule(_, { candidateId, scheduleId, date, employeeId }) {
    const response = await candidate.rescheduleTimelineSchedule(candidateId, scheduleId, {
      date,
      employee_id: employeeId,
    });
    if (response && response.status === 200) {
      return { ok: true, data: response.data };
    }
    return { ok: false, response };
  },
  async fetchDocuments({ commit }, id) {
    commit(types.PENDING);
    const payload = await candidate.fetchDocuments(id);
    if ((await payload.status) === 200) {
      commit(types.FETCH_CANDIDATE_DOCUMENTS, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },

  // Candidate-level documents actions (new system)
  async fetchCandidateDocuments({ commit }, candidateId) {
    commit(types.PENDING);
    const payload = await candidate.getCandidateDocuments(candidateId);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE_DOCUMENTS, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async uploadCandidateDocument({ commit }, { candidateId, formData }) {
    commit(types.PENDING);
    const payload = await candidate.uploadCandidateDocument(candidateId, formData);
    if ((await payload.status) === 200) {
      commit(types.ADD_CANDIDATE_DOCUMENT, {
        candidate_id: candidateId,
        document: payload.data.document
      });
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },
  async removeCandidateDocument({ commit }, { candidateId, documentId, source, applicationId }) {
    commit(types.PENDING);
    const payload = await candidate.removeCandidateDocument(documentId, source, applicationId);
    if ((await payload.status) === 200) {
      commit(types.REMOVE_CANDIDATE_DOCUMENT, {
        candidate_id: candidateId,
        document_id: documentId,
        source
      });
    }
    commit(types.ACCOUNT_MSG, await payload);
    return payload;
  },

  async sendComment({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.sendComment(message);
    if ((await payload.status) === 200) {
      commit(types.SEND_COMMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async updateComment({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.updateComment(message);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_COMMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async deleteComment({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.deleteComment(message.id);
    if ((await payload.status) === 200) {
      commit(types.DELETE_COMMENT, message);
    }
  },
  async duplicateCandidate({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.duplicateCandidate(message);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async committeeCandidate({ commit }, message) {
    commit(types.PENDING);
    const payload = await candidate.committeeCandidate(message);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async fetchEvents({ commit }, id) {
    commit(types.PENDING);
    const payload = await candidate.fetchEvents(id);
    if ((await payload.status) === 200) {
      commit(types.FETCH_CANDIDATE_EVENTS, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addEvent({ commit }, event) {
    commit(types.PENDING);
    const payload = await candidate.addEvent(event);
    if ((await payload.status) === 200) {
      commit(types.CANDIDATE_EVENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async addJpoEvent({ commit }, event) {
    commit(types.PENDING);
    const payload = await candidate.addJpoEvent(event);
    if ((await payload.status) === 200) {
      commit(types.CANDIDATE_EVENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async delEvent({ commit }, event) {
    commit(types.PENDING);
    const payload = await candidate.delEvent(event);
    if ((await payload.status) === 200) {
      commit(types.FETCH_CANDIDATE_EVENTS, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async receivedPayment({ commit }, payment) {
    commit(types.PENDING);
    const payload = await candidate.receivedPayment(payment);
    if ((await payload.status) === 200) {
      commit(types.RECEIVED_PAYMENT, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async receivedRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.receivedRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.RECEIVED_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptAlternanceRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptAlternanceRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.RECEIVED_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async cancelRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.cancelRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveOfpCheck({ commit }, checks) {
    commit(types.PENDING);
    const payload = await candidate.saveOfpCheck(checks);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveVisaTransfer({ commit }, checks) {
    commit(types.PENDING);
    const payload = await candidate.saveVisaTransfer(checks);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async saveGuarantor({ commit }, checks) {
    commit(types.PENDING);
    const payload = await candidate.saveGuarantor(checks);
    if ((await payload.status) === 200) {
      commit(types.UPDATE_REGISTRATION, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptOrderRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptOrderRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptOrderNoRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptOrderNoRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
  async acceptOrderOfferRegistration({ commit }, registration) {
    commit(types.PENDING);
    const payload = await candidate.acceptOrderOfferRegistration(registration);
    if ((await payload.status) === 200) {
      commit(types.SET_CANDIDATE, await payload.data);
    }
    commit(types.ACCOUNT_MSG, await payload);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
